/**
 * Account list.
 *
 * Search is a server-side filter over email and nickname; the page number and
 * the query travel in the URL so a filtered list survives a refresh and can be
 * shared.
 *
 * The collapsed row shows identity, activation and suspension state. The opened
 * part holds the relationship details that are only needed when an operator is
 * investigating one account, plus the way into the detail page.
 */

import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { TFunction } from 'i18next'

import { LoadingBlock, ErrorBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { EmptyState } from '../../components/ui/primitives'
import { useAdminUsers } from '../../hooks/use-admin'
import { formatUnixSeconds, joinList } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, CollapsibleRow, DetailRow, Pager, StateBadge, isBanned, usePageParam } from './shared'

/** The login methods an account has used, joined with the locale's separator. */
function loginMethodLabels(
  methods: readonly ('email' | 'github')[],
  t: TFunction,
): string {
  return methods.length === 0
    ? t('common.unknown')
    : joinList(
        methods.map((method) => t(`admin.users.loginMethod.${method}`)),
        t,
      )
}

export function AdminUsersPage() {
  const { t } = useTranslation()
  const { page, params, update, goToPage } = usePageParam()
  const query = params.get('query') ?? ''
  const [draft, setDraft] = useState(query)
  const locale = currentLocale()

  const users = useAdminUsers(query, page)

  /** Runs the typed query: the form submit, the Enter key and the button all use it. */
  function runSearch() {
    update({ query: draft, page: '1' })
  }

  const pager = users.data ? (
    <Pager
      page={users.data.page}
      pageSize={users.data.pageSize}
      total={users.data.total}
      onPage={goToPage}
    />
  ) : null

  return (
    <AdminPage
      title={t('admin.users.title')}
      description={t('admin.users.description')}
      current={t('admin.nav.users')}
    >
      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-center"
        onSubmit={(event) => {
          event.preventDefault()
          runSearch()
        }}
      >
        {/*
          The box carries its name as an accessible label instead of a visible one:
          the only visible「搜索」is the submit button, so clicking the text a
          visitor reads as the search control always runs the search.
        */}
        <Input
          aria-label={t('admin.users.searchLabel')}
          value={draft}
          placeholder={t('admin.users.searchPlaceholder')}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            // Enter runs the same search the button does, whether or not the
            // browser turns the key press into a form submission.
            if (event.key === 'Enter') {
              event.preventDefault()
              runSearch()
            }
          }}
        />
        <Button type="submit" variant="outline" size="sm" className="shrink-0 self-start">
          {t('admin.users.search')}
        </Button>
      </form>

      {users.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {users.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void users.refetch()}
        />
      ) : null}
      {users.data && users.data.items.length === 0 ? (
        <EmptyState title={t('admin.users.empty')} />
      ) : null}

      {pager}

      <div className="flex flex-col gap-2">
        {users.data?.items.map((user) => (
          <CollapsibleRow
            key={user.id}
            title={<span className="truncate text-sm font-medium">{user.email}</span>}
            meta={
              <span className="text-muted-foreground truncate text-xs">
                {user.nickname ?? t('admin.users.nicknameEmpty')} ·{' '}
                {t('admin.users.registeredAt')} {formatUnixSeconds(user.createdAt, locale)}
              </span>
            }
            badges={
              <>
                {user.isAdmin ? (
                  <StateBadge label={t('admin.users.administrator')} variant="default" />
                ) : null}
                <StateBadge
                  label={user.isInvited ? t('admin.users.activated') : t('admin.users.notActivated')}
                  variant={user.isInvited ? 'success' : 'outline'}
                />
                {isBanned(user.bannedUntil) ? (
                  <StateBadge label={t('admin.users.suspended')} variant="destructive" />
                ) : null}
              </>
            }
          >
            <DetailRow label={t('admin.users.loginMethods')}>
              {loginMethodLabels(user.loginMethods, t)}
            </DetailRow>
            <DetailRow label={t('admin.users.referralCode')}>{user.referralCode}</DetailRow>
            <DetailRow label={t('admin.users.referrer')}>
              {user.referrerEmail ?? t('admin.users.noReferrer')}
            </DetailRow>
            {user.referralChannel ? (
              <DetailRow label={t('admin.users.referralChannel')}>
                {t(`admin.referralChannel.${user.referralChannel}`)}
              </DetailRow>
            ) : null}
            {user.bannedUntil ? (
              <>
                <DetailRow label={t('admin.users.bannedUntil')}>
                  {formatUnixSeconds(user.bannedUntil, locale)}
                </DetailRow>
                {user.banReason ? (
                  <DetailRow label={t('admin.users.banReason')}>{user.banReason}</DetailRow>
                ) : null}
              </>
            ) : null}
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to={`/admin/users/${user.id}`}>{t('admin.users.open')}</Link>
              </Button>
            </div>
          </CollapsibleRow>
        ))}
      </div>

      {pager}
    </AdminPage>
  )
}
